import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Alert,
  ApiError,
  QrCode,
  Spinner,
  cn,
  looksLikeEmail,
  useDialogFocus,
  useModalPresence,
  type SelfEnrollCode,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { useOrg } from "./OrgContext";
import { useReferralMint } from "./useReferralMint";
import iconCopyLink from "../assets/icon-copy-link.svg";
import iconForm from "../assets/icon-form.svg";

/**
 * The optional cost-tool estimate context. When the modal is opened from the
 * cost calculator with a live estimate, the email send carries these so the
 * backend recomputes the estimate server-side and attaches the cost-report
 * PDF. It mirrors the `/cost-tool/estimate` request shape (`planId`, `rxcuis`,
 * optional `quantities`, optional `planYear`); the backend never trusts
 * client-sent numbers, only these inputs. When absent (the tab-bar entry
 * points), the email send posts just the address, so the link goes out with no
 * attachment.
 */
export interface ReferEstimateContext {
  planId: string;
  rxcuis: string[];
  quantities?: Record<string, number>;
  /** The plan year the estimate ran under; absent means this year. */
  planYear?: number;
}

export interface ReferSomeoneModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  /** Present only on the cost-tool handoff path; see `ReferEstimateContext`. */
  estimateContext?: ReferEstimateContext | undefined;
}

/**
 * The one shared referral dialog (Figma dR8cJ6hRSW5eERToo64boe, node
 * 2340:320). Every "refer a patient" entry point (HomeTab's card,
 * `EnrollmentsTab`'s button, the cost tool's `HandoffSection`) opens it.
 * The patient column offers the patient's own channels, and the staff column
 * completes the form together on this device.
 *
 * The patient column follows the design's order: the QR code and copyable
 * link first, the email row below them.
 *
 * Two link types, one per audience:
 * - The patient channels (email, QR, copy) carry the org's persistent
 *   self-enroll link (`?selfId=`: multi-use, no expiry, resolved from the
 *   database). The patient opens it on their own phone and time, so a link
 *   that burns on first use or expires within minutes would fail them. It is
 *   fetched once per open from `GET .../self-enroll-code`, which provisions it
 *   lazily.
 * - The staff channel ("Open the form on this device") mints a single-use,
 *   short-lived refid JWT (`?refId=`) through `useReferralMint`. It mints on
 *   click, because a refid is consumed on access and opening the modal must
 *   burn none. The refid keeps the staff user's attribution and never reaches
 *   a patient channel.
 *
 * No calculator or patient state rides on either URL. The optional
 * `estimateContext` rides only on the email send (as `planId` + `rxcuis`), so
 * the backend can recompute and attach the anonymous cost-report PDF; the URLs
 * stay context-free.
 *
 * The dialog shell is local because the shared `Modal` has a fixed-width panel
 * with no width option; it shares that component's mechanics through
 * `useDialogFocus` (scroll lock, Escape, Tab wrap, focus return) and
 * `useModalPresence` (open and close motion), plus the portal, a backdrop
 * click, `aria-modal` and `aria-labelledby`.
 */
export function ReferSomeoneModal({
  open,
  onClose,
  orgId,
  estimateContext,
}: ReferSomeoneModalProps): ReactNode {
  const api = useApi();
  // The org name personalizes the standing-link fine print ("Referrals are
  // associated to <org>"); every mount point sits under `OrgShell`.
  const { res } = useOrg();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const { mounted, closing, onAnimationEnd } = useModalPresence(open);
  useDialogFocus({ open, panelRef, onClose });

  const [url, setUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { mint, minting, error: mintError } = useReferralMint(orgId);

  // The email row doesn't use `url`: the endpoint builds the same standing
  // self-enroll link server-side, so only the typed address leaves this device.
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const emailInputId = useId();

  // Generation counter: a fetch resolving after the modal was closed (or after
  // a newer fetch started) must not surface its stale link.
  const genRef = useRef(0);
  const runFetch = useCallback(async (): Promise<void> => {
    const gen = ++genRef.current;
    setUrl(null);
    setLoadError(null);
    try {
      const code = await api<SelfEnrollCode>(`/api/partner/orgs/${orgId}/self-enroll-code`);
      if (genRef.current === gen) setUrl(code.url);
    } catch (err: unknown) {
      if (genRef.current !== gen) return;
      // A 401 already cleared the session in `useApi`, and the login redirect
      // takes over. Anything else shows the alert and Retry below.
      if (!(err instanceof ApiError && err.status === 401)) {
        setLoadError("We couldn't load the referral link. Please try again.");
      }
    }
  }, [api, orgId]);

  // Fetch exactly once per open (the ref guards StrictMode's double effect run);
  // closing resets the guard so the next open re-fetches.
  const fetchedThisOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      fetchedThisOpen.current = false;
      genRef.current += 1; // invalidate any in-flight fetch
      return;
    }
    if (fetchedThisOpen.current) return;
    fetchedThisOpen.current = true;
    setCopied(false);
    setEmail("");
    setSentTo(null);
    setSendError(null);
    void runFetch();
  }, [open, runFetch]);

  const onSendEmail = async (): Promise<void> => {
    setSending(true);
    setSendError(null);
    try {
      // With a live estimate, send the plan and drugs so the backend recomputes
      // the estimate and attaches the cost-report PDF; without it, the address
      // alone, and the plain link goes out. `quantities` and `planYear` ride
      // only when set.
      const body =
        estimateContext === undefined
          ? { email }
          : {
              email,
              planId: estimateContext.planId,
              rxcuis: estimateContext.rxcuis,
              ...(estimateContext.quantities !== undefined &&
              Object.keys(estimateContext.quantities).length > 0
                ? { quantities: estimateContext.quantities }
                : {}),
              ...(estimateContext.planYear !== undefined
                ? { planYear: estimateContext.planYear }
                : {}),
            };
      await api(`/api/partner/orgs/${orgId}/refer-email`, {
        method: "POST",
        body,
      });
      setSentTo(email);
    } catch (err: unknown) {
      // A 401 already cleared the session in `useApi`, and the login redirect
      // takes over. Anything else shows the inline error and re-enables Send.
      if (!(err instanceof ApiError && err.status === 401)) {
        setSendError("We couldn't send the email. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (url === null) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // The clipboard is unavailable (permissions or a non-secure context). The
      // link stays selectable in its field, so this fails quietly.
    }
  };

  // Staff-assisted path: mint the single-use refid on click, not on open, and
  // open it in a new tab. A failed mint lands in `mintError`.
  const onOpenHere = async (): Promise<void> => {
    const minted = await mint();
    if (minted !== null) window.open(minted, "_blank", "noopener,noreferrer");
  };

  if (!mounted) return null;

  const close = (): void => {
    if (open) onClose();
  };

  const node = (
    <div
      className={cn(
        "bg-midnight/40 fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-6 backdrop-blur-sm sm:items-center",
        open ? "motion-safe:animate-modal-backdrop-in" : "motion-safe:animate-modal-backdrop-out",
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      inert={closing}
      aria-hidden={closing || undefined}
      // Only a click on the backdrop itself closes; one bubbling up from the panel doesn't.
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "bg-ice w-full max-w-[980px] rounded-2xl p-6 shadow-xl sm:p-7",
          open ? "motion-safe:animate-modal-panel-in" : "motion-safe:animate-modal-panel-out",
        )}
        onAnimationEnd={onAnimationEnd}
      >
        <div className="flex items-start justify-between gap-4">
          <h2
            id={titleId}
            className="text-foreground font-serif text-[26px] font-semibold leading-9 sm:text-[30px]"
          >
            Refer patient to DearBill
          </h2>
          <button
            type="button"
            onClick={close}
            className="bg-soft text-midnight hover:bg-soft-hover shrink-0 cursor-pointer whitespace-nowrap rounded-lg px-6 py-3.5 text-base font-semibold leading-[1.5] transition"
          >
            Close
          </button>
        </div>

        {loadError !== null ? (
          <div className="mt-5 flex flex-col gap-4">
            <Alert tone="error" title={loadError}>
              If it keeps happening, use &ldquo;Need help? Call&rdquo; at the top of the page.
            </Alert>
            <button
              type="button"
              onClick={() => void runFetch()}
              className="bg-deep-navy cursor-pointer self-start whitespace-nowrap rounded-lg px-6 py-3.5 text-base font-bold leading-[1.5] text-white transition hover:opacity-80"
            >
              Retry
            </button>
          </div>
        ) : url === null ? (
          <div className="text-slate-teal flex items-center justify-center gap-3 py-16 text-base">
            <Spinner /> <span>Getting the referral link…</span>
          </div>
        ) : (
          <>
            <div className="mt-5 grid items-stretch gap-5 md:grid-cols-2">
              {/* The patient column leads with the QR code and copyable link,
                  then the email row (node 2340:339's order). */}
              <section className="border-haze flex flex-col gap-5 rounded-2xl border bg-white p-6">
                <div>
                  <h3 className="text-foreground text-[22px] font-semibold leading-[30px]">
                    Patient completes info
                  </h3>
                  <p className="text-foreground mt-1.5 text-[17px] leading-[25px]">
                    Provide the QR code or send link.
                  </p>
                </div>

                <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                  <div className="border-haze shrink-0 self-center rounded-2xl border bg-white p-3 sm:self-start">
                    <QrCode url={url} size={150} title="Referral QR code" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-2">
                      <input
                        readOnly
                        value={url}
                        aria-label="Referral link"
                        onFocus={(e) => e.currentTarget.select()}
                        className="border-haze text-foreground min-h-11 w-full rounded-lg border bg-white px-3 text-[15px]"
                      />
                      <button
                        type="button"
                        onClick={() => void onCopy()}
                        className="bg-deep-navy inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg px-5 py-3 text-[15px] font-bold leading-[1.5] text-white transition hover:opacity-80"
                      >
                        <img src={iconCopyLink} alt="" aria-hidden="true" className="size-4" />
                        {copied ? "Copied" : "Copy the link"}
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  {sentTo !== null ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-success text-base font-semibold">✓ Sent to {sentTo}</p>
                      <button
                        type="button"
                        onClick={() => {
                          setEmail("");
                          setSentTo(null);
                          setSendError(null);
                        }}
                        className="text-ember hover:text-ember-dark cursor-pointer font-semibold underline underline-offset-2"
                      >
                        Send another
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* The design labels the email row by its placeholder
                          alone; the label stays for screen readers. */}
                      <label htmlFor={emailInputId} className="sr-only">
                        Email them the link
                      </label>
                      <div className="flex items-stretch gap-3">
                        <input
                          id={emailInputId}
                          type="email"
                          value={email}
                          placeholder="patient@email.com"
                          onChange={(e) => setEmail(e.target.value)}
                          className="border-haze text-foreground min-h-[46px] w-full min-w-0 flex-1 rounded-lg border bg-white px-4 text-[17px]"
                        />
                        <button
                          type="button"
                          onClick={() => void onSendEmail()}
                          disabled={sending || !looksLikeEmail(email)}
                          className="bg-deep-navy min-h-[46px] shrink-0 cursor-pointer whitespace-nowrap rounded-lg px-7 text-base font-bold leading-[1.5] text-white transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {sending ? "Sending…" : "Send"}
                        </button>
                      </div>
                      {sendError !== null && (
                        <p role="alert" className="text-danger mt-1.5 text-[13px] leading-[1.5]">
                          {sendError}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* CostToolTab.test.tsx and ReferSomeoneModal.test.tsx pin this
                    caption verbatim. It states the standing link's lifetime, so
                    nobody mistakes it for the single-use on-device form. */}
                <p className="text-slate-teal mt-auto text-[15px] leading-[22px]">
                  This code never expires and can be used by any number of patients. Referrals are
                  associated to {res.name}.
                </p>
              </section>

              {/* The staff column mints a single-use refid link on click; it
                  never goes to a patient. */}
              <section className="border-haze flex flex-col gap-4 rounded-2xl border bg-white p-6">
                <h3 className="text-foreground text-[22px] font-semibold leading-[30px]">
                  {res.profile.organizationType} completes info with patient
                </h3>

                <div className="bg-frost text-deep-navy rounded-lg p-[18px] text-[17px]">
                  <p className="font-semibold leading-6">What you will need from them</p>
                  <p className="mt-1 leading-[25px]">
                    Their name, date of birth, phone number, and their red, white, and blue Medicare
                    card.
                  </p>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => void onOpenHere()}
                    disabled={minting}
                    className="bg-ember inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg px-8 py-4 text-lg font-bold leading-[1.5] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <img src={iconForm} alt="" aria-hidden="true" className="size-5" />
                    {minting ? "Opening…" : "Open form"}
                  </button>
                  {/* The 10 minutes states the true refid TTL
                      (`refid_ttl_seconds` in server/app/config.py). */}
                  <p className="text-foreground mt-2 text-[15px] leading-[22px]">
                    For security, this form only stays active for 10 minutes and accepts a single
                    submission. Referrals are credited to the submitter.
                  </p>
                  {mintError !== null && (
                    <p role="alert" className="text-danger mt-1.5 text-[13px] leading-[1.5]">
                      {mintError}
                    </p>
                  )}
                </div>

                <div className="bg-haze h-px w-full" />

                <h4 className="text-foreground text-[19px] font-semibold leading-7">
                  What happens next
                </h4>
                <ol className="flex flex-col gap-3">
                  <Step n={1}>DearBill calls the patient, usually within one business day.</Step>
                  <Step n={2}>
                    Our team checks every program the patient may qualify for and manages the
                    paperwork.
                  </Step>
                  <Step n={3}>
                    You can track progress on the{" "}
                    <Link
                      to={`/orgs/${orgId}/forms`}
                      onClick={onClose}
                      className="text-ember hover:text-ember-dark underline underline-offset-2"
                    >
                      Referrals
                    </Link>{" "}
                    tab. We&rsquo;ll also let you know if something needs your attention.
                  </Step>
                </ol>
              </section>
            </div>

            <p className="text-slate-teal mt-4 text-[15px] leading-[22px]">
              You will see progress for each patient. You will not see medical records, income, or
              what other medications they take.
            </p>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

/** One numbered "What happens next" step. */
function Step({ n, children }: { n: number; children: ReactNode }): ReactNode {
  return (
    <li className="flex gap-4">
      <span className="text-ember text-lg font-semibold leading-[26px]">{n}</span>
      <span className="text-foreground min-w-0 flex-1 text-[17px] leading-[26px]">{children}</span>
    </li>
  );
}
